/**
 * Phase 0 (П35Р instrumentation) — counters + cumulative timings exposed
 * for Playwright smoke harness readout via `window.__import_metrics`.
 *
 * Cheap mutate-in-place counters; no allocations on the hot path. Bumps
 * happen inside `appendToCompactCacheInternal`, `snapshotSpaceEntities*`,
 * cache mutations, Zustand set wrapper, dxfWorkerClient handlers, and
 * runSceneBulkLoad.
 *
 * Smoke runner reads via `await page.evaluate(() => window.__import_metrics)`
 * after the import deadline elapses (or on success). Output goes to the
 * smoke report JSON for offline analysis.
 *
 * Default-off: all `record*` helpers are no-op unless `enable()` was
 * called. Production builds skip overhead entirely.
 */

let enabled = false;

export interface ImportMetrics {
  /** appendToCompactCacheInternal calls during import (silent + non-silent). */
  appendCount: number;
  /** Cumulative ms spent inside appendToCompactCacheInternal. */
  appendTimeMs: number;
  /** snapshotSpaceEntitiesFromHolder calls (full per-space iteration). */
  snapshotCount: number;
  /** Cumulative ms spent inside snapshotSpaceEntitiesFromHolder. */
  snapshotTimeMs: number;
  /** holder.cacheVersion bumps. */
  cacheVersionBumps: number;
  /** holder.metadataIndexVersion bumps (separate from cacheVersion). */
  metadataIndexVersionBumps: number;
  /** Number of `useEditorStore.setState(...)` / `set(...)` invocations. */
  setStoreCalls: number;
  /** Worker → main "progress" messages received. */
  workerProgressMsgs: number;
  /** Worker → main "entity-chunk" messages received. */
  workerChunkMsgs: number;
  /** Cumulative entities streamed via chunks. */
  workerChunkEntities: number;
  /** Cumulative ms spent inside runSceneBulkLoad. */
  sceneBulkLoadMs: number;
  /** runSceneBulkLoad calls. */
  sceneBulkLoadCount: number;
  /** Cumulative ms spent inside __entityMetadataIndex.bulkLoad. */
  metadataBulkLoadMs: number;
  /** Wall-clock t0 set by `enable()`. ms since page load. */
  t0Ms: number;
  /** Wall-clock at last record* call. ms since page load. */
  tLastMs: number;
}

const m: ImportMetrics = {
  appendCount: 0,
  appendTimeMs: 0,
  snapshotCount: 0,
  snapshotTimeMs: 0,
  cacheVersionBumps: 0,
  metadataIndexVersionBumps: 0,
  setStoreCalls: 0,
  workerProgressMsgs: 0,
  workerChunkMsgs: 0,
  workerChunkEntities: 0,
  sceneBulkLoadMs: 0,
  sceneBulkLoadCount: 0,
  metadataBulkLoadMs: 0,
  t0Ms: 0,
  tLastMs: 0,
};

const now = (): number =>
  typeof performance !== "undefined" && typeof performance.now === "function"
    ? performance.now()
    : Date.now();

const tick = (): void => {
  m.tLastMs = now();
};

/** Enable + reset all counters. Call at start of an import flow. */
export function enable(): void {
  enabled = true;
  m.appendCount = 0;
  m.appendTimeMs = 0;
  m.snapshotCount = 0;
  m.snapshotTimeMs = 0;
  m.cacheVersionBumps = 0;
  m.metadataIndexVersionBumps = 0;
  m.setStoreCalls = 0;
  m.workerProgressMsgs = 0;
  m.workerChunkMsgs = 0;
  m.workerChunkEntities = 0;
  m.sceneBulkLoadMs = 0;
  m.sceneBulkLoadCount = 0;
  m.metadataBulkLoadMs = 0;
  m.t0Ms = now();
  m.tLastMs = m.t0Ms;
}

/** Disable. Counters remain readable for one final dump. */
export function disable(): void {
  enabled = false;
}

/** Snapshot of current counters. Cheap shallow copy. */
export function snapshot(): ImportMetrics {
  return { ...m };
}

export const recordAppend = (durationMs: number): void => {
  if (!enabled) return;
  m.appendCount += 1;
  m.appendTimeMs += durationMs;
  tick();
};

/**
 * Codex round-5 #13 — bulk variant for batched-streaming-append path.
 *
 * `appendBatchToCompactCacheSilent` previously called `recordAppend()`
 * once per appended entity, which paid per-call overhead even when
 * metrics were disabled (default). The batched variant collapses the
 * loop into a single counter bump + cumulative time, preserving the
 * monotonic `appendCount` semantics while eliminating per-entity
 * call-site overhead on the streaming hot path.
 *
 * Caller passes the total appended count and the total time spent
 * inside the loop (NOT per-entity average); the function divides
 * internally only for telemetry consistency with the per-call API.
 */
export const recordAppendBatch = (
  count: number,
  totalDurationMs: number,
): void => {
  if (!enabled) return;
  if (count <= 0) return;
  m.appendCount += count;
  m.appendTimeMs += totalDurationMs;
  tick();
};

/**
 * Read-only accessor for the enable flag. Hot-path callers can use
 * this to short-circuit metric-collection work (e.g. avoid a
 * per-entity loop that only feeds `recordAppend`) when metrics are
 * disabled — the default state in production builds.
 */
export const isImportMetricsEnabled = (): boolean => enabled;

export const recordSnapshot = (durationMs: number): void => {
  if (!enabled) return;
  m.snapshotCount += 1;
  m.snapshotTimeMs += durationMs;
  tick();
};

export const recordCacheVersionBump = (): void => {
  if (!enabled) return;
  m.cacheVersionBumps += 1;
  tick();
};

export const recordMetadataIndexVersionBump = (): void => {
  if (!enabled) return;
  m.metadataIndexVersionBumps += 1;
  tick();
};

export const recordSetStoreCall = (): void => {
  if (!enabled) return;
  m.setStoreCalls += 1;
  tick();
};

export const recordWorkerProgressMsg = (): void => {
  if (!enabled) return;
  m.workerProgressMsgs += 1;
  tick();
};

export const recordWorkerChunkMsg = (entityCount: number): void => {
  if (!enabled) return;
  m.workerChunkMsgs += 1;
  m.workerChunkEntities += entityCount;
  tick();
};

export const recordSceneBulkLoad = (durationMs: number): void => {
  if (!enabled) return;
  m.sceneBulkLoadCount += 1;
  m.sceneBulkLoadMs += durationMs;
  tick();
};

export const recordMetadataBulkLoad = (durationMs: number): void => {
  if (!enabled) return;
  m.metadataBulkLoadMs += durationMs;
  tick();
};

/**
 * Install global accessor `window.__import_metrics` for Playwright readout.
 * Calls a fresh `snapshot()` on each access so the runner sees up-to-date
 * counters without re-installing.
 */
export function installGlobalAccessor(): void {
  if (typeof globalThis === "undefined") return;
  Object.defineProperty(globalThis, "__import_metrics", {
    configurable: true,
    enumerable: false,
    get() {
      return snapshot();
    },
  });
  Object.defineProperty(globalThis, "__import_metrics_enable", {
    configurable: true,
    enumerable: false,
    value: enable,
  });
  Object.defineProperty(globalThis, "__import_metrics_disable", {
    configurable: true,
    enumerable: false,
    value: disable,
  });
}
